import React from 'react';
import { CAREER_DATA } from '../utils/constants';
import SectionHeader from './SectionHeader';

var tones = {
  current: {
    dot: 'bg-green-500',
    cardBorder: 'border-green-500/25',
    cardBg: 'bg-green-500/5',
    badge: 'bg-green-500/15 text-green-500',
    title: 'text-green-500',
  },
  past: {
    dot: 'bg-primary-500',
    cardBorder: 'border-primary-500/25',
    cardBg: 'bg-primary-500/5',
    badge: 'bg-primary-500/15 text-primary-500',
    title: 'text-primary-500',
  },
  academic: {
    dot: 'bg-purple-500',
    cardBorder: 'border-purple-500/25',
    cardBg: 'bg-purple-500/5',
    badge: 'bg-purple-500/15 text-purple-500',
    title: 'text-purple-500',
  },
};

var COMPANY_LOGOS = {
  ey: {
    src: (process.env.PUBLIC_URL || '') + '/icons/companies/ey.jpg',
    alt: 'EY logo',
    wrap: 'bg-black',
    size: 'w-8 h-8',
  },
  apt: {
    src: (process.env.PUBLIC_URL || '') + '/icons/companies/apt.png',
    alt: 'APT Information Systems logo',
    wrap: 'bg-white',
    size: 'w-8 h-8',
  },
  hmu: {
    src: (process.env.PUBLIC_URL || '') + '/icons/companies/hmu.png',
    alt: 'Hellenic Mediterranean University logo',
    wrap: 'bg-white',
    size: 'w-8 h-8',
  },
};

var Logo = ({ item }) => {
  var logo = COMPANY_LOGOS[item.logo];
  if (!logo) {
    return (
      <div className="w-12 h-12 rounded-xl bg-surface-700/50 border border-surface-600/40 flex items-center justify-center text-surface-300 font-bold text-sm">
        {(item.company || '?').slice(0, 2).toUpperCase()}
      </div>
    );
  }

  return (
    <div className={'w-12 h-12 rounded-xl border border-surface-600/40 overflow-hidden flex items-center justify-center ' + logo.wrap}>
      <img src={logo.src} alt={logo.alt} width="32" height="32" className={logo.size + ' object-contain'} loading="lazy" />
    </div>
  );
};

var CareerCard = ({ item }) => {
  var tone = tones[item.type] || tones.past;

  return (
    <div className={'card rounded-2xl border p-5 sm:p-6 ' + tone.cardBg + ' ' + tone.cardBorder}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <Logo item={item} />
          <div>
            <h3 className="text-heading text-xl font-black leading-tight">{item.title || item.role}</h3>
            <p className={tone.title + ' text-sm font-bold mt-1'}>{item.company}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {item.employmentType && (
            <span className={'px-2.5 py-1 rounded-full text-xs font-bold ' + tone.badge}>{item.employmentType}</span>
          )}
          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-surface-700/50 text-surface-300">
            {item.type === 'current' ? 'Current' : 'Completed'}
          </span>
        </div>
      </div>

      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-surface-500 text-sm font-medium mb-4 tabular-nums">
        <span className="text-heading">{item.period}</span>
        <span className="text-surface-700" aria-hidden="true">·</span>
        <span>{item.duration}</span>
        {item.location && (
          <>
            <span className="text-surface-700" aria-hidden="true">·</span>
            <span>{item.location}</span>
          </>
        )}
      </p>

      {item.roles && item.roles.length > 0 && (
        <div className="mb-4 rounded-xl border border-surface-700/30 bg-surface-900/30 p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3.5">
            <p className="text-surface-500 text-xs font-bold uppercase tracking-wider">Role Progression</p>
            <span className="text-[11px] font-semibold text-surface-600">{item.roles.length} positions</span>
          </div>
          <div className="relative pl-7">
            <div className="absolute left-[6px] top-2 bottom-2 w-px bg-gradient-to-b from-surface-600/60 to-surface-700/30" />
            <div className="space-y-4">
              {item.roles.map(function(role, idx) {
                return (
                  <div key={role.title} className="relative">
                    <div className={'absolute -left-[26px] top-1 w-3 h-3 rounded-full ring-4 ring-surface-900 ' + (role.current ? tone.dot + ' animate-pulse' : 'bg-surface-600')} />
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-heading text-sm font-bold leading-tight">{role.title}</span>
                      {role.promoted && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/15 text-amber-500">
                          <svg aria-hidden="true" className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
                          </svg>
                          Promoted
                        </span>
                      )}
                      {role.current && (
                        <span className={'px-2 py-0.5 rounded-full text-[10px] font-bold ' + tone.badge}>Current</span>
                      )}
                    </div>
                    <p className="text-surface-500 text-xs mt-1 font-medium">{role.period} <span className="text-surface-600">·</span> {role.duration}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {(item.summary || item.description) && (
        <p className="text-surface-400 text-sm leading-relaxed mb-4">{item.summary || item.description}</p>
      )}

      {item.courses && item.courses.length > 0 && (
        <div className="mb-4">
          <p className="text-surface-500 text-xs font-bold uppercase tracking-wider mb-2">Taught Courses</p>
          <div className="grid sm:grid-cols-2 gap-1.5">
            {item.courses.map(function(course) {
              return (
                <div key={course} className="text-surface-400 text-xs border border-surface-700/30 bg-surface-900/30 rounded-md px-2.5 py-1.5">
                  {course}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {item.highlights && item.highlights.length > 0 && (
        <ul className="space-y-1.5 mb-4">
          {item.highlights.map(function(highlight) {
            return (
              <li key={highlight} className="flex items-start gap-2 text-surface-500 text-xs">
                <span className={tone.title + ' font-bold'}>+</span>
                <span>{highlight}</span>
              </li>
            );
          })}
        </ul>
      )}

      {item.technologies && item.technologies.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {item.technologies.map(function(tech) {
            return (
              <span key={tech} className="px-2 py-0.5 text-xs font-semibold bg-surface-700/30 text-surface-400 rounded-md">
                {tech}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

var Career = () => (
  <section id="career" className="py-20">
    <div className="max-w-7xl 2xl:max-w-[88rem] mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeader
        eyebrow="Professional Journey"
        title="Career Timeline"
        subtitle="Consolidated timeline of engineering and teaching roles with clear scope, dates, and responsibilities."
      />

      <div className="relative max-w-5xl mx-auto">
        <div className="absolute left-5 top-0 bottom-0 w-px bg-surface-700/50" />
        <div className="space-y-7">
          {CAREER_DATA.map(function(item) {
            var tone = tones[item.type] || tones.past;
            return (
              <div key={item.id} className="relative pl-12">
                <div className={'absolute left-[14px] top-7 w-3 h-3 rounded-full ring-4 ring-surface-950 ' + tone.dot + (item.type === 'current' ? ' animate-pulse' : '')} />
                <CareerCard item={item} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

export default Career;
